import React, { useState, useEffect } from 'react';
import { Zap, Users, UserCheck, Workflow, RefreshCw } from "lucide-react";
import ApiService from '../../services/apiService';

const HeroSection = ({ onNavigate }) => {
  const [agents, setAgents] = useState([
    {
      id: 1,
      name: "Workflow Engine",
      isLive: true,
      icon: Workflow,
      description: "AI-powered workflow automation",
      agentKey: 'workflow'
    },
    {
      id: 2,
      name: "Content Generator",
      isLive: false,
      icon: Zap,
      description: "AI educational content creator",
      agentKey: 'vidya'
    },
    {
      id: 3, 
      name: "Student Assistant",
      isLive: true,
      icon: Users,
      description: "Student-support assistant",
      agentKey: 'vidyarthi'
    },
    {
      id: 4,
      name: "Teacher Coach",
      isLive: false,
      icon: UserCheck,
      description: "Teacher-coaching companion",
      agentKey: 'shikshak'
    },
  ]);
  const [loading, setLoading] = useState(false);

  const apiService = new ApiService();

  const checkAgentHealth = async () => {
    try {
      setLoading(true);
      
      // Make real API calls to check each agent's health
      const healthPromises = [
        apiService.makeApiCall('vidya', '/api/health', null, 'GET').then(result => ({ agent: 'vidya', status: 'healthy', data: result })).catch(err => ({ agent: 'vidya', status: 'unhealthy', error: err.message })),
        apiService.makeApiCall('vidyarthi', '/api/health', null, 'GET').then(result => ({ agent: 'vidyarthi', status: 'healthy', data: result })).catch(err => ({ agent: 'vidyarthi', status: 'unhealthy', error: err.message })),
        apiService.makeApiCall('shikshak', '/health', null, 'GET').then(result => ({ agent: 'shikshak', status: 'healthy', data: result })).catch(err => ({ agent: 'shikshak', status: 'unhealthy', error: err.message }))
      ];
      
      const healthResults = await Promise.allSettled(healthPromises);
      
      setAgents(prevAgents => 
        prevAgents.map(agent => {
          if (agent.agentKey === 'workflow') {
            return { ...agent, isLive: true }; // Workflow engine is always live
          }
          
          const healthResult = healthResults.find(result => 
            result.status === 'fulfilled' && result.value?.agent === agent.agentKey
          );
          
          const isHealthy = healthResult?.status === 'fulfilled' && healthResult.value?.status === 'healthy';
          
          return {
            ...agent,
            isLive: isHealthy
          };
        })
      );
    } catch (error) {
      console.error('Failed to check agent health:', error);
      // On error, mark all external agents as offline
      setAgents(prevAgents => 
        prevAgents.map(agent => ({
          ...agent,
          isLive: agent.agentKey === 'workflow' // Only workflow stays live
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAgentHealth();
    // Check health every 30 seconds
    const interval = setInterval(checkAgentHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
      <header className="mb-6">

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Welcome to Sahayak</h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor your educational workflow automation and AI agents
            </p>
          </div>
          <button 
            onClick={checkAgentHealth}
            disabled={loading}
            className="text-blue-600 hover:text-blue-700 p-2 rounded transition-colors duration-200 disabled:opacity-50"
            title="Refresh Agent Status"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <h1 className="text-3xl font-bold text-slate-800">Welcome to Sahayak</h1>
        <p className="text-sm text-slate-500 mt-1">
          Monitor your educational workflow automation and AI agents
        </p>

      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {agents.map((agent) => (
          <div
            key={agent.id}
            className="border border-slate-200 rounded-lg p-5 transition hover:shadow-lg hover:bg-purple-100 cursor-pointer"
            onClick={() => {
              if (agent.name === "Workflow Engine") {
                onNavigate && onNavigate({ id: 'workflow', label: 'Workflow Editor' });
              } else if (agent.name === "Content Generator") {
                onNavigate && onNavigate({ id: 'generate-content', label: 'Generate Content' });
              }
            }}
          >
            <div className="flex items-start justify-between">
              <agent.icon className="w-7 h-7 text-blue-600" />
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    agent.isLive ? "bg-green-500" : "bg-slate-300"
                  }`}
                />
                <span className="text-xs font-medium text-slate-600">
                  {agent.isLive ? "Live" : "Offline"}
                </span>
              </div>
            </div>

            <h2 className="mt-3 font-semibold text-slate-800">{agent.name}</h2>
            <p className="text-sm text-slate-500 mt-1">{agent.description}</p>
          </div>
        ))}
      </div>
         <elevenlabs-convai agent-id="agent_2401k13ahm9dfbkrym3wx3yee4vw"></elevenlabs-convai>
    </section>
  );
};

export default HeroSection;